/**
 * The sidebar (the prototype's `sidebar()`): in the reader — "Views" with +, the folders and saved views
 * with their counts, Corrections with its open count, Settings and the account identity; in settings —
 * the nine categories and "Back to reader".
 *
 * Fix 2: counts are content-sized (`Count`): compact from 10,000 with the exact number as tooltip; in the
 * icon rail they become badges capped at 99+. Fix 8: folder and view icons take no colour.
 */

import { useNavigate } from 'react-router-dom';
import { Icon } from '../icons/Icon.js';
import { Count } from '../ui/Count.js';
import { formatBadge, formatExact } from '../ui/format.js';
import { Tool } from '../ui/Tool.js';
import { settingsCategories } from '../settings/registry.js';
import { openDialog } from './dialogs.js';
import { useCounts, useFolders, useIdentity, useViews } from './hooks.js';
import { ui } from './ui-store.js';

export type SidebarSelection = { kind: 'folder'; id: string } | { kind: 'view'; id: string } | { kind: 'corrections' } | { kind: 'settings'; category: string };

function NavCount({ value }: { value: number | undefined }) {
  if (value === undefined) return null;
  return (
    <>
      <Count value={value} />
      {value > 0 ? (
        <i className="nav-badge" aria-hidden="true" title={formatExact(value)}>
          {formatBadge(value)}
        </i>
      ) : null}
    </>
  );
}

export function Sidebar({ selection }: { selection: SidebarSelection }) {
  const navigate = useNavigate();
  const isSettings = selection.kind === 'settings';
  const go = (to: string): void => {
    ui.set({ mobileOverlay: 'none' });
    navigate(to);
  };
  return (
    <aside className="navigation">
      <div className="navigation-heading">
        <span>{isSettings ? 'Settings' : 'Views'}</span>
        {!isSettings ? <Tool icon="plus" label="Create view" onClick={() => openDialog('view-builder', {})} /> : null}
      </div>
      <nav aria-label={isSettings ? 'Settings categories' : 'Views'}>{isSettings ? <CategoryRows active={selection.category} go={go} /> : <ViewRows selection={selection} go={go} />}</nav>
      <div className="nav-bottom">
        {isSettings ? (
          <button type="button" className="nav-row" title="Back to reader" aria-label="Back to reader" onClick={() => go('/')}>
            <Icon name="arrow-left" />
            <span>Back to reader</span>
          </button>
        ) : (
          <>
            <CorrectionsRow active={selection.kind === 'corrections'} go={go} />
            <button type="button" className="nav-row" title="Settings" aria-label="Settings" onClick={() => go('/settings/sources')}>
              <Icon name="settings" />
              <span>Settings</span>
            </button>
          </>
        )}
        <AccountControl go={go} />
      </div>
    </aside>
  );
}

function CategoryRows({ active, go }: { active: string; go(to: string): void }) {
  return (
    <>
      {settingsCategories().map((c) => (
        <button
          type="button"
          key={c.id}
          className={active === c.id ? 'nav-row active' : 'nav-row'}
          title={c.name}
          aria-label={c.name}
          aria-current={active === c.id ? 'page' : undefined}
          data-category={c.id}
          onClick={() => go(`/settings/${c.id}`)}
        >
          <Icon name={c.icon} />
          <span>{c.name}</span>
        </button>
      ))}
    </>
  );
}

function ViewRows({ selection, go }: { selection: SidebarSelection; go(to: string): void }) {
  const folders = useFolders();
  const views = useViews();
  const counts = useCounts();
  return (
    <>
      {folders.map((f) => {
        const active = selection.kind === 'folder' && selection.id === f.id;
        return (
          <button
            type="button"
            key={f.id}
            className={active ? 'nav-row active' : 'nav-row'}
            title={f.name}
            aria-label={f.name}
            aria-current={active ? 'page' : undefined}
            data-folder={f.id}
            onClick={() => go(`/f/${encodeURIComponent(f.id)}`)}
          >
            <span className="nav-icon">
              <Icon name={f.icon} />
            </span>
            <span>{f.name}</span>
            <NavCount value={counts?.folders[f.id]?.total} />
          </button>
        );
      })}
      {views.map((v) => {
        const active = selection.kind === 'view' && selection.id === v.id;
        return (
          <button
            type="button"
            key={v.id}
            className={active ? 'nav-row active' : 'nav-row'}
            title={v.name}
            aria-label={v.name}
            aria-current={active ? 'page' : undefined}
            data-view={v.id}
            onClick={() => go(`/v/${encodeURIComponent(v.id)}`)}
          >
            <span className="nav-icon">
              <Icon name={v.icon} />
            </span>
            <span>{v.name}</span>
            <NavCount value={counts?.views[v.id]?.total} />
          </button>
        );
      })}
    </>
  );
}

function CorrectionsRow({ active, go }: { active: boolean; go(to: string): void }) {
  const counts = useCounts();
  const open = counts?.openCorrections ?? 0;
  return (
    <button
      type="button"
      className={active ? 'nav-row nav-corrections active' : 'nav-row nav-corrections'}
      title="Corrections"
      aria-label={open ? `Corrections, ${formatExact(open)} open` : 'Corrections'}
      aria-current={active ? 'page' : undefined}
      onClick={() => go('/corrections')}
    >
      <span className="nav-icon">
        <Icon name="undo-2" />
      </span>
      <span>Corrections</span>
      {open > 0 ? <NavCount value={open} /> : null}
    </button>
  );
}

function AccountControl({ go }: { go(to: string): void }) {
  const identity = useIdentity();
  return (
    <button
      type="button"
      className="account-control"
      title={`${identity.name} · ${identity.plan} plan`}
      aria-label={`Account: ${identity.name}, ${identity.plan} plan`}
      onClick={() => go('/settings/account/profile')}
    >
      <span className="avatar">{identity.initials}</span>
      <span className="account-identity">
        <strong>{identity.name}</strong>
        <span>
          <b>{identity.plan} plan</b>
        </span>
      </span>
      <Icon name="chevron-up" />
    </button>
  );
}
